import { ColorScheme } from '@unifyapps/defs/types/interface';
import { useEffect, useState } from 'react';

/**
 * Custom hook to detect and listen for system color scheme changes
 * Returns 'light', 'dark', or null if not available
 */
export function useSystemColorScheme(): ColorScheme.LIGHT | ColorScheme.DARK {
  const [colorScheme, setColorScheme] = useState<ColorScheme.LIGHT | ColorScheme.DARK>(() => {
    // SSR safe initial value
    if (typeof window === 'undefined') return ColorScheme.LIGHT;

    // Check initial system preference
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
      return ColorScheme.DARK;
    }
    if (window.matchMedia?.('(prefers-color-scheme: light)').matches) {
      return ColorScheme.LIGHT;
    }
    return ColorScheme.LIGHT;
  });

  useEffect(() => {
    // Ensure we're in the browser
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const lightModeQuery = window.matchMedia('(prefers-color-scheme: light)');

    // Update color scheme based on current preference
    const updateColorScheme = () => {
      if (darkModeQuery.matches) {
        setColorScheme(ColorScheme.DARK);
      } else if (lightModeQuery.matches) {
        setColorScheme(ColorScheme.LIGHT);
      } else {
        setColorScheme(ColorScheme.LIGHT);
      }
    };

    // Set initial value
    updateColorScheme();

    // Listen for changes
    darkModeQuery.addEventListener('change', updateColorScheme);
    lightModeQuery.addEventListener('change', updateColorScheme);

    return () => {
      darkModeQuery.removeEventListener('change', updateColorScheme);
      lightModeQuery.removeEventListener('change', updateColorScheme);
    };
  }, []);

  return colorScheme;
}
